/** Directory locations as pins; shares the list's filtered input so a pin and a row never disagree. */
import React, { useEffect, useMemo, useRef, useState } from "react"

import { AlertTriangle, Clock, MapPin } from "lucide-react"
import { MapRef, Marker } from "react-map-gl/mapbox"

import { MAP_PANEL, MAPBOX_TOKEN, MapShell, MapUnavailable } from "@/components/shared/MapShell"
import { Badge } from "@/components/ui/badge"
import { CoveredLocation, pinnableLocations } from "@/lib/collectionCoverage"
import { boundsOf, edgePadding, type LngLat, OVERLAP_DEGREES } from "@/lib/mapGeometry"
import { cn } from "@/lib/utils"
import { getSlotTypeDotClasses, getSlotTypeLabel, getSlotTypePinClasses } from "@/lib/slotTypeDisplay"
import { Customer, SlotType } from "@/types/collections"

/** Greater London, for the empty case. */
const DEFAULT_VIEW = { longitude: -0.1276, latitude: 51.5074, zoom: 9 }

/** So a single pin, or a cluster at one depot, doesn't fit-bounds to street level. */
const MIN_BOUNDS_DEGREES = 0.02
const BOUNDS_PADDING_PX = 60

/** Co-located sites collapse into one pin whose card lists them. */
interface Pin {
  key: string
  lat: number
  lng: number
  entries: CoveredLocation[]
}

/** Two sites within ~10m of each other are one place with two records, and share a pin. */
function buildPins(covered: CoveredLocation[]): Pin[] {
  const pins: Pin[] = []
  for (const entry of covered) {
    const { latitude: lat, longitude: lng } = entry.location
    const near = pins.find(
      (pin) =>
        Math.abs(pin.lat - lat) < OVERLAP_DEGREES && Math.abs(pin.lng - lng) < OVERLAP_DEGREES,
    )
    if (near) near.entries.push(entry)
    else pins.push({ key: entry.location.id, lat, lng, entries: [entry] })
  }
  return pins
}

/** The type a pin takes its colour from: the first slot of the day at the site. */
function leadSlotType(pin: Pin): SlotType | undefined {
  for (const entry of pin.entries) {
    if (entry.slots.length > 0) return entry.slots[0].slot_type
  }
  return undefined
}

interface DirectoryMapViewProps {
  /** Already filtered — the same array the list shows. */
  covered: CoveredLocation[]
  customersById: globalThis.Map<string, Customer>
  onSelectLocation?: (locationId: string) => void
  filterDescription?: string
}

const DirectoryMapView: React.FC<DirectoryMapViewProps> = ({
  covered,
  customersById,
  onSelectLocation,
  filterDescription,
}) => {
  const mapRef = useRef<MapRef>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  const pinnable = useMemo(() => pinnableLocations(covered), [covered])
  const missingCoordinates = covered.length - pinnable.length
  const pins = useMemo(() => buildPins(pinnable), [pinnable])

  const legendTypes = useMemo(() => {
    const types = new Set<SlotType>()
    for (const { slots } of pinnable) for (const slot of slots) types.add(slot.slot_type)
    return [...types].sort()
  }, [pinnable])

  const customerNamesFor = (entry: CoveredLocation): string[] => {
    const ids = entry.location.customers?.map((lc) => lc.customer_id) ?? []
    const all = ids.length > 0 ? ids : entry.location.customer_id ? [entry.location.customer_id] : []
    return all.map((id) => customersById.get(id)?.name).filter((name): name is string => !!name)
  }

  const boundsKey = pins.map((p) => p.key).join("|")
  useEffect(() => {
    const bounds = boundsOf(
      pins.map((p): LngLat => [p.lng, p.lat]),
      MIN_BOUNDS_DEGREES,
    )
    if (!mapRef.current || !mapLoaded || !bounds) return
    mapRef.current.fitBounds(bounds, { padding: edgePadding(BOUNDS_PADDING_PX), duration: 400 })
    // Keyed on boundsKey so an identical re-render doesn't re-frame a map someone just panned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, mapLoaded])

  if (!MAPBOX_TOKEN) {
    return <MapUnavailable className="h-full" />
  }

  return (
    <div className="relative h-full min-h-0 w-full">
      <MapShell mapRef={mapRef} initialViewState={DEFAULT_VIEW} onLoad={() => setMapLoaded(true)}>
        {pins.map((pin) => {
          const stacked = pin.entries.length > 1
          return (
            <Marker key={pin.key} longitude={pin.lng} latitude={pin.lat} anchor="bottom">
              <div className="group relative">
                {stacked && (
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 rounded border border-palette-jungleGreen200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-palette-jungleGreen700 shadow-sm">
                    {pin.entries.length}
                  </span>
                )}
                <MapPin
                  size={26}
                  className={cn(getSlotTypePinClasses(leadSlotType(pin), stacked))}
                />
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 group-hover:block">
                  <div className="max-h-[320px] min-w-[180px] max-w-[300px] overflow-y-auto rounded border border-palette-jungleGreen200 bg-white p-2 text-left shadow-lg">
                    {pin.entries.map((entry, index) => {
                      const names = customerNamesFor(entry)
                      return (
                        <div
                          key={entry.location.id}
                          className={cn(
                            index > 0 && "mt-2 border-t border-palette-jungleGreen200 pt-2",
                          )}
                        >
                          <div className="text-xs font-semibold text-palette-jungleGreen900">
                            {entry.location.site_name}
                          </div>
                          <div className="truncate text-[10px] text-palette-jungleGreen500">
                            {entry.location.address}, {entry.location.postcode}
                          </div>
                          {names.length > 0 && (
                            <div className="mt-0.5 truncate text-[10px] text-blue-600">
                              {names.join(", ")}
                            </div>
                          )}
                          {entry.slots.length > 0 ? (
                            <div className="mt-1 space-y-0.5">
                              {entry.slots.map((slot) => (
                                <div
                                  key={slot.id}
                                  className="flex items-center gap-1 text-[10px] text-palette-jungleGreen600"
                                >
                                  <span
                                    className={cn(
                                      "h-2 w-2 flex-shrink-0 rounded-full border",
                                      getSlotTypeDotClasses(slot.slot_type),
                                    )}
                                  />
                                  <Clock size={9} className="flex-shrink-0" />
                                  <span className="font-medium">
                                    {slot.start_time}–{slot.end_time}
                                  </span>
                                  <span className="truncate text-palette-jungleGreen400">
                                    {getSlotTypeLabel(slot.slot_type)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 text-[10px] italic text-palette-jungleGreen400">
                              No active slots
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {/* A stack has no single record to open, so only a lone pin is clickable. */}
                {!stacked && onSelectLocation && (
                  <button
                    type="button"
                    aria-label={`Open ${pin.entries[0].location.site_name}`}
                    onClick={() => onSelectLocation(pin.entries[0].location.id)}
                    className="absolute inset-0 cursor-pointer"
                  />
                )}
              </div>
            </Marker>
          )
        })}
      </MapShell>

      <div
        className={cn(
          "pointer-events-none absolute left-3 top-3 z-10 max-w-[260px] p-2.5 shadow-sm",
          MAP_PANEL,
        )}
      >
        <div className="flex items-center gap-1.5 text-xs font-medium text-palette-jungleGreen900">
          <MapPin size={12} className="text-palette-jungleGreen400" />
          {pinnable.length} {pinnable.length === 1 ? "location" : "locations"}
          {filterDescription ? (
            <span className="font-normal text-palette-jungleGreen500">· {filterDescription}</span>
          ) : null}
        </div>
        {missingCoordinates > 0 && (
          <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-700">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            <span>
              {missingCoordinates} not shown — no coordinates
            </span>
          </div>
        )}
        {legendTypes.length > 0 && (
          <div className="mt-2 space-y-0.5 border-t border-palette-jungleGreen200 pt-2">
            {legendTypes.map((type) => (
              <div
                key={type}
                className="flex items-center gap-1.5 text-[10px] text-palette-jungleGreen600"
              >
                <span
                  className={cn("h-2 w-2 rounded-full border", getSlotTypeDotClasses(type))}
                />
                {getSlotTypeLabel(type)}
              </div>
            ))}
          </div>
        )}
      </div>

      {pins.length === 0 && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className={cn("px-4 py-3 text-center shadow-sm", MAP_PANEL)}>
            <p className="text-sm text-palette-jungleGreen600">
              {filterDescription
                ? `Nothing to collect ${filterDescription.startsWith("from") || filterDescription.startsWith("until") ? filterDescription : `on ${filterDescription}`}`
                : "No locations to show"}
            </p>
            {missingCoordinates > 0 && (
              <Badge variant="outline" className="mt-1.5 text-[10px]">
                {missingCoordinates} without coordinates
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DirectoryMapView
